<template>
  <form @submit.prevent="handleFormSubmit">
    <ProductOptions :options="product.options" />

    <div
      v-if="
        v$.selectedOptions.$dirty &&
        v$.selectedOptions.$invalid &&
        hasMultipleVariants
      "
      class="input"
    >
      <div class="input__response">
        <ul>
          <li v-for="error of v$.selectedOptions.$errors" :key="error.$uid">
            {{ error.$message }}
          </li>
        </ul>
      </div>
    </div>

    <template v-if="!hideSizeGuide">
      <div v-if="hasMultipleVariants" class="size-guide__wrapper">
        <IconButton icon="ruler" transparent @click="setOpenSizeGuide(true)"
          >View size guide</IconButton
        >
        <IconButton icon="question" transparent easysize>
          Find your right size
        </IconButton>
      </div>
      <div class="easysize_placeholder"></div>
    </template>

    <p v-if="variantHasLowStock" class="low-stock-message">
      Only {{ variant.quantityAvailable }} left in stock
    </p>

    <Button type="submit" full-width data-testid="add-to-bag">
      <template v-if="availableForSale">
        Add to bag
        <template v-if="variantHasLowStock">(low stock)</template>
      </template>
      <template v-else> Sold out. Notify me when back in stock </template>
      |
      {{ $filters.toCurrency(price.amount, price.currencyCode, 0) }}
      <template v-if="isOnSale">
        <span class="onsale">{{
          $filters.toCurrency(
            originalPrice.amount,
            originalPrice.currencyCode,
            0
          )
        }}</span>
      </template>
    </Button>

    <Transition
      name="expand"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div
        v-if="!hideDynamicCheckoutButtons"
        v-show="showDynamicCheckoutButtons"
        ref="dynamic-payment-buttons-target"
      ></div>
    </Transition>
  </form>

  <BagFailure
    v-model:open="openBagFailure"
    message="We're trying to make our sales as fair as possible so we've limited each order to a maximum of two sizes per style and a maximum of six pieces in total"
  />

  <AddedToBag v-model:open="openAddedToBag" :variant="variant" />

  <StockNotification
    v-model:open="openStockNotification"
    :variant="selectedVariant || {}"
  />
</template>

<script>
import Button from "@/components/Button.vue";
import IconButton from "@/components/IconButton.vue";
import ProductOptions from "@/components/product-display/ProductOptions.vue";
import { mapActions, mapState } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { businessLogic } from "@/config";
import * as Sentry from "@sentry/vue";
import AddedToBag from "@/components/add-to-bag/AddedToBag.vue";
import StockNotification from "@/components/StockNotification.vue";
import BagFailure from "@/components/add-to-bag/BagFailure.vue";

export default {
  name: "AddToBag",
  components: {
    Button,
    IconButton,
    AddedToBag,
    ProductOptions,
    StockNotification,
    BagFailure,
  },
  props: {
    hideSizeGuide: {
      type: Boolean,
      default: false,
    },
    hideDynamicCheckoutButtons: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      cartId: null,
      openAddedToBag: false,
      openStockNotification: false,
      openBagFailure: false,
      showDynamicCheckoutButtons: false,
      showEasySizeButton: false,
    };
  },
  computed: {
    ...mapState("product", [
      "selectedOptions",
      "selectedVariant",
      "defaultVariant",
      "product",
    ]),
    variant() {
      return this.selectedVariant || this.defaultVariant;
    },
    price() {
      return this.variant?.priceV2;
    },
    originalPrice() {
      return this.product.compareAtPriceRange.minVariantPrice;
    },
    isOnSale() {
      return (
        parseFloat(this.originalPrice.amount) > parseFloat(this.price.amount)
      );
    },
    availableForSale() {
      return this.variant?.availableForSale;
    },
    variantHasLowStock() {
      return (
        this.variant.quantityAvailable > 0 &&
        this.variant.quantityAvailable < businessLogic.lowStockQuantity
      );
    },
    hasMultipleVariants() {
      const options = this.product.options;
      return options.some((option) => option.values.length > 1);
    },
  },
  watch: {
    selectedVariant(val) {
      if (val && !this.hideDynamicCheckoutButtons) {
        this.showDynamicCheckoutButtons = true;
        this.setVariantIdInput();
      }
    },
    availableForSale(val) {
      if (!val) {
        this.showDynamicCheckoutButtons = false;
      }
    },
  },
  validations() {
    return {
      selectedOptions: {
        required: helpers.withMessage("Please enter a size above", required),
      },
    };
  },
  mounted() {
    if (!this.hideDynamicCheckoutButtons) {
      this.setVariantIdInput();
      this.moveDynamicPaymentButton();
    }
    const observer = new MutationObserver(this.checkBodyClass);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  },
  methods: {
    ...mapActions("add-to-bag", ["setProduct"]),
    ...mapActions("cart", ["addCartItem", "checkCartRules"]),
    ...mapActions("product", ["setOpenSizeGuide"]),
    checkBodyClass() {
      if (document.body.classList.contains("es-loaded")) {
        this.showEasySizeButton = true;
      } else {
        this.showEasySizeButton = false;
      }
    },
    enter(element) {
      const width = getComputedStyle(element).width;

      element.style.width = width;
      element.style.position = "absolute";
      element.style.visibility = "hidden";
      element.style.height = "auto";

      const height = getComputedStyle(element).height;

      element.style.width = null;
      element.style.position = null;
      element.style.visibility = null;
      element.style.height = 0;

      getComputedStyle(element).height;

      requestAnimationFrame(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
    leave(element) {
      const height = getComputedStyle(element).height;

      element.style.height = height;

      getComputedStyle(element).height;

      requestAnimationFrame(() => {
        element.style.height = 0;
      });
    },
    setVariantIdInput() {
      try {
        const dynamicPaymentButtons = document.getElementById(
          "dynamic-payment-buttons"
        );

        const input = dynamicPaymentButtons.querySelector(
          ".product-variant-id"
        );

        const variantId = this.variant.id.split(
          "gid://shopify/ProductVariant/"
        )[1];

        input.value = variantId;
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    moveDynamicPaymentButton() {
      const dynamicPaymentButtons = document.getElementById(
        "dynamic-payment-buttons"
      );

      const target = this.$refs["dynamic-payment-buttons-target"];

      target.append(dynamicPaymentButtons);
    },
    async handleFormSubmit() {
      console.log("submit");
      this.v$.selectedOptions.$touch();

      if (this.hasMultipleVariants && this.v$.selectedOptions.$invalid) {
        return;
      }

      this.setProduct(this.product);

      if (!this.variant.availableForSale) {
        return (this.openStockNotification = true);
      }

      const cartRuleResults = await this.checkCartRules(this.product.id);
      if (!cartRuleResults) {
        this.openBagFailure = true;
        return;
      } else {
        this.openAddedToBag = true;
      }

      try {
        await this.addCartItem({ id: this.variant.id });
      } catch (error) {
        Sentry.captureException(error);
        console.log(error);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/css/components/form";

.onsale {
  text-decoration: line-through;
}

.expand-enter-active,
.expand-leave-active {
  transition: height 0.25s ease-in-out;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
}

.size-guide__wrapper {
  display: flex;
  justify-content: space-between;
  margin: 10px 0 20px 0;

  ::v-deep(.button--icon) {
    @include t__utility-body-10;
    padding: 2px 0;
    text-transform: none;
    display: flex;
    align-items: center;

    .icon {
      margin-right: 7px;
      font-size: 24px;

      &-question {
        font-size: 16px;
      }
    }
  }

  ::v-deep(.easysize_button) {
    margin: 0;
    text-align: right;
    &:before {
      display: none;
    }
  }

  ::v-deep(.button:first-child) {
    text-align: left;
  }
}

.low-stock-message {
  color: $color-red;
  @include t__utility-message-30;
  margin-bottom: 15px;
}
</style>

<style lang="scss">
.shopify-cleanslate {
  & > div.size--large,
  & > div.size--small {
    font-size: 9px !important;
    text-transform: uppercase;
    font-weight: 600 !important;
    letter-spacing: 0.49px;
    margin-top: 10px;
  }
}

.shopify-payment-button__more-options {
  display: none !important;
}
</style>
